import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Flex,
  Text,
  Icon,
  IconButton,
  Button,
  Spinner,
  Tooltip,
} from "@chakra-ui/react";
import {
  HiOutlinePaperClip,
  HiOutlineArrowDownTray,
  HiOutlinePlus,
  HiOutlineTrash,
  HiOutlineDocument,
} from "react-icons/hi2";
import SendAttachmentModal from "./SendAttachmentModal";
import { BASE_API_URL } from "../Components/config";
import { c } from "./classroomColors";

const API = `${BASE_API_URL}/api/v1`;

/**
 * Classroom "Attachments" section. A teacher sends a file to a specific student;
 * that student sees it here under their own attachments. Instructors see every
 * file they've sent (grouped by student) and can remove them.
 */
const AttachmentsSection = ({ courseGuid, isInstructor, isDark = true }) => {
  const [attachments, setAttachments] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const token = localStorage.getItem("accessToken");
  const authHeaders = { Authorization: `Bearer ${token}` };

  const fetchAttachments = useCallback(async () => {
    if (!courseGuid) return;
    try {
      const res = await fetch(`${API}/student-attachments/by-course-guid/${courseGuid}`, {
        headers: authHeaders,
      });
      if (res.ok) setAttachments(await res.json());
    } catch (e) {
      /* soft-fail */
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseGuid]);

  useEffect(() => {
    setLoading(true);
    fetchAttachments();
  }, [fetchAttachments]);

  useEffect(() => {
    if (!isInstructor || !courseGuid) return;
    (async () => {
      try {
        const res = await fetch(`${API}/user-course-links/students-by-course-guid/${courseGuid}`, {
          headers: authHeaders,
        });
        if (res.ok) setStudents(await res.json());
      } catch (e) {
        /* soft-fail */
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInstructor, courseGuid]);

  const handleDelete = async (guid) => {
    setAttachments((prev) => prev.filter((a) => a.guid !== guid));
    try {
      await fetch(`${API}/student-attachments/${guid}`, { method: "DELETE", headers: authHeaders });
    } catch (e) {
      fetchAttachments();
    }
  };

  /**
   * Save the file under the name it was uploaded with. The plain `download`
   * attribute is ignored for cross origin links, so the browser would fall back
   * to the S3 key (which is not a usable filename). Pulling the bytes down and
   * saving them ourselves also rescues files that were uploaded before the key
   * format was fixed. If S3 blocks the fetch, just open the file instead.
   */
  const handleDownload = async (attachment) => {
    const name = attachment.filename || "attachment";
    try {
      const res = await fetch(attachment.s3_url);
      if (!res.ok) throw new Error(String(res.status));
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = name;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(objectUrl);
    } catch (e) {
      window.open(attachment.s3_url, "_blank", "noopener,noreferrer");
    }
  };

  const recipientName = (a) =>
    [a.recipient_first_name, a.recipient_last_name].filter(Boolean).join(" ").trim() || "Student";

  return (
    <Box
      mt="20px"
      bg={c.surface}
      border="1px solid"
      borderColor={c.borderSoft}
      borderRadius="12px"
      padding="20px 24px"
    >
      <Flex justify="space-between" align="center" mb="14px">
        <Flex align="center" gap="8px">
          <Icon as={HiOutlinePaperClip} boxSize="18px" color={c.textSub} />
          <Text fontSize="16px" fontWeight="600" color={c.text}>
            Attachments
          </Text>
        </Flex>
        {isInstructor && (
          <Button
            leftIcon={<Icon as={HiOutlinePlus} boxSize="15px" />}
            size="sm"
            variant="outline"
            borderRadius="8px"
            fontSize="12.5px"
            fontWeight="500"
            color={c.text}
            borderColor={c.inputBorder}
            _hover={{ bg: c.hover }}
            onClick={() => setModalOpen(true)}
          >
            Send file
          </Button>
        )}
      </Flex>

      {loading ? (
        <Flex justify="center" py="20px">
          <Spinner size="sm" color={c.accent} />
        </Flex>
      ) : attachments.length === 0 ? (
        <Text fontSize="13px" color={c.textMuted} fontStyle="italic" py="6px">
          {isInstructor
            ? "No files sent yet. Use “Send file” to share a document with a specific student."
            : "No files have been shared with you yet."}
        </Text>
      ) : (
        <Flex direction="column" gap="8px">
          {attachments.map((a) => (
            <Flex
              key={a.guid}
              align="center"
              justify="space-between"
              gap="12px"
              padding="12px 14px"
              borderRadius="10px"
              border="1px solid"
              borderColor={c.borderSoft}
              background={c.surfaceSubtle}
            >
              <Flex align="center" gap="10px" minWidth="0">
                <Icon as={HiOutlineDocument} boxSize="18px" color={c.textSub} flexShrink={0} />
                <Box minWidth="0">
                  <Text fontSize="13.5px" fontWeight="500" color={c.text} noOfLines={1}>
                    {a.filename}
                  </Text>
                  <Text fontSize="11px" color={c.textMuted}>
                    {isInstructor ? `To ${recipientName(a)} · ` : ""}
                    {new Date(a.created_date).toLocaleDateString()}
                  </Text>
                </Box>
              </Flex>

              <Flex align="center" gap="4px" flexShrink={0}>
                <Tooltip label="Download" hasArrow placement="top" fontSize="11px">
                  <IconButton
                    aria-label={`Download ${a.filename}`}
                    icon={<Icon as={HiOutlineArrowDownTray} boxSize="16px" />}
                    size="sm"
                    variant="ghost"
                    color={c.textSub}
                    _hover={{ bg: c.hover, color: c.accent }}
                    onClick={() => handleDownload(a)}
                  />
                </Tooltip>
                {isInstructor && (
                  <Tooltip label="Remove" hasArrow placement="top" fontSize="11px">
                    <IconButton
                      aria-label="Remove"
                      icon={<Icon as={HiOutlineTrash} boxSize="15px" />}
                      size="sm"
                      variant="ghost"
                      color={c.textMuted}
                      _hover={{ bg: c.hover }}
                      onClick={() => handleDelete(a.guid)}
                    />
                  </Tooltip>
                )}
              </Flex>
            </Flex>
          ))}
        </Flex>
      )}

      {isInstructor && (
        <SendAttachmentModal
          isOpen={modalOpen}
          onClose={() => setModalOpen(false)}
          courseGuid={courseGuid}
          students={students}
          isDark={isDark}
          onSent={(created) => setAttachments((prev) => [created, ...prev])}
        />
      )}
    </Box>
  );
};

export default AttachmentsSection;
